import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="nested-select"
export default class extends Controller {
  static targets = ["affiliateSelect", "categorySelect", "categoryContainer", "loadingIndicator"];
  static values = {
    url: String,
    currentCategoryId: Number,
  };

  connect() {
    // Set the URL for fetching categories
    this.urlValue = "/admin/affiliates/:id/categories";

    // Preserve selected category if form has errors
    if (!this.hasCurrentCategoryIdValue) {
      this.currentCategoryIdValue = parseInt(this.categorySelectTarget.value) || null;
    }

    // Initialize state based on current affiliate selection
    this.initializeState();
  }

  initializeState() {
    const affiliateId = this.affiliateSelectTarget.value;

    if (affiliateId) {
      this.loadCategories();
    } else {
      this.resetCategorySelect();
    }
  }

  async loadCategories() {
    const affiliateId = this.affiliateSelectTarget.value;

    if (!affiliateId) {
      this.resetCategorySelect();
      return;
    }

    try {
      this.showLoading();

      const url = this.urlValue.replace(":id", affiliateId);
      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      this.populateCategorySelect(data.categories);
    } catch (error) {
      console.error("Failed to load categories:", error);
      this.showError("Failed to load categories. Please try again.");
    } finally {
      this.hideLoading();
    }
  }

  populateCategorySelect(categories) {
    const select = this.categorySelectTarget;

    // Clear existing options
    select.innerHTML = "";

    // Add blank option
    const blankOption = new Option("Select a category...", "", true, false);
    blankOption.disabled = true;
    select.appendChild(blankOption);

    // Add category options
    categories.forEach((category) => {
      const option = new Option(category.name, category.id);

      // Restore previous selection if available
      if (this.currentCategoryIdValue === category.id) {
        option.selected = true;
      }

      select.appendChild(option);
    });

    // Enable the select and improve UX
    this.enableCategorySelect();

    // Show success feedback
    this.showSuccessFeedback(categories.length);
  }

  resetCategorySelect() {
    const select = this.categorySelectTarget;
    select.innerHTML = '<option value="" disabled selected>Select an affiliate first...</option>';
    select.disabled = true;
    select.classList.add("opacity-50");
    this.currentCategoryIdValue = null;
  }

  enableCategorySelect() {
    const select = this.categorySelectTarget;
    select.disabled = false;
    select.classList.remove("opacity-50");

    // Add smooth transition
    select.style.transition = "opacity 0.2s ease-in-out";
  }

  showLoading() {
    this.loadingIndicatorTarget.classList.remove("hidden");
    this.categorySelectTarget.disabled = true;
    this.categorySelectTarget.classList.add("opacity-50");
  }

  hideLoading() {
    this.loadingIndicatorTarget.classList.add("hidden");
  }

  showError(message) {
    // Create or update error message
    let errorElement = this.categoryContainerTarget.querySelector(".error-message");

    if (!errorElement) {
      errorElement = document.createElement("div");
      errorElement.className = "error-message text-red-600 text-sm mt-1";
      this.categoryContainerTarget.appendChild(errorElement);
    }

    errorElement.textContent = message;

    // Auto-hide after 5 seconds
    setTimeout(() => {
      if (errorElement.parentNode) {
        errorElement.remove();
      }
    }, 5000);
  }

  showSuccessFeedback(count) {
    // Brief visual feedback for successful load
    const select = this.categorySelectTarget;
    const originalBorder = select.style.borderColor;

    select.style.borderColor = "#10B981"; // green-500
    select.style.transition = "border-color 0.3s ease";

    setTimeout(() => {
      select.style.borderColor = originalBorder;
    }, 1000);

    // Optional: Show count in console for debugging
    console.log(`Loaded ${count} categories`);
  }

  // Handle form resets
  disconnect() {
    this.currentCategoryIdValue = null;
  }
}
